import { useQuery } from '@tanstack/react-query'
import { Check } from 'lucide-react'
import { useId, useState } from 'react'
import { useNavigate } from 'react-router'
import { FormError } from '@/components/FormError'
import { Avatar } from '@/components/tree/Avatar'
import { Button } from '@/components/ui/button'
import { api } from '@/lib/api'
import { useInviteToCircle, WATCH_MS } from '@/lib/connections'
import { useFamilies } from '@/lib/families'
import type { WaitingInvite } from '@/lib/invites'
import { useMe } from '@/lib/useMe'

type Face = { id: string; name: string; portrait: string | null; src?: string }

// The moment two people are connected, on both phones: their faces come together inside one ring, and then a quiet
// choice. Staying connected is enough (each sees what the other shares with Everyone); or you can ask them into one of
// your circles, and they're asked whether to join. While this is open, an invite from them shows up here too.
export function Together({ other, onDone }: { other: Face; onDone: () => void }) {
  const me = useMe().data
  const [choosing, setChoosing] = useState(false)
  const ring = useId()
  const hasCircles = (useFamilies().data?.families ?? []).some((x) => !x.personal && !x.everyone)
  const first = other.name.trim().split(/\s+/)[0] || other.name
  if (!me) return null
  return (
    <div className="flex flex-col items-center gap-6 text-center">
      <div className="together" aria-hidden>
        <svg viewBox="0 0 200 120" className="together-ring">
          <defs>
            <linearGradient id={ring} x1="0" x2="1" y1="0" y2="0">
              <stop offset="0" style={{ stopColor: 'var(--logo-1)' }} />
              <stop offset="0.5" style={{ stopColor: 'var(--logo-2)' }} />
              <stop offset="1" style={{ stopColor: 'var(--logo-3)' }} />
            </linearGradient>
          </defs>
          <rect x="3" y="3" width="194" height="114" rx="57" pathLength={100} stroke={`url(#${ring})`} />
        </svg>
        <FaceOf person={{ id: me.id, name: me.name, portrait: me.portrait }} className="together-me" />
        <FaceOf person={other} className="together-them" />
      </div>
      <div className="together-words flex flex-col gap-2" role="status">
        <h2 className="text-title text-balance">You and {first} are connected</h2>
        <p className="text-sub text-balance text-muted-foreground">
          You each see what the other shares with Everyone. Either of you can end it any time.
        </p>
      </div>
      <InviteFrom other={other} />
      <div className="together-words flex w-full flex-col gap-2">
        {choosing ? (
          <CirclePicker other={other} first={first} onDone={onDone} />
        ) : (
          <>
            {me.canInvite && hasCircles && (
              <Button variant="outline" onClick={() => setChoosing(true)}>
                Invite {first} to a circle
              </Button>
            )}
            <Button onClick={onDone}>Just stay connected</Button>
          </>
        )}
      </div>
    </div>
  )
}

function FaceOf({ person, className }: { person: Face; className: string }) {
  if (person.src)
    return (
      <span className={`${className} overflow-hidden rounded-full bg-secondary shadow-paper`}>
        <img src={person.src} alt="" className="size-full object-cover" />
      </span>
    )
  return <Avatar person={person} variant="display" className={`${className} text-4xl`} />
}

// Your circles, to ask them into one. The circle they're already in says so when you pick it.
function CirclePicker({ other, first, onDone }: { other: Face; first: string; onDone: () => void }) {
  const circles = (useFamilies().data?.families ?? []).filter((x) => !x.personal && !x.everyone)
  const invite = useInviteToCircle()
  const [sent, setSent] = useState<string[]>([])
  if (circles.length === 0)
    return (
      <>
        <p className="text-sub text-muted-foreground">
          You aren’t in a circle yet. Once you are, you can invite {first}.
        </p>
        <Button onClick={onDone}>Done</Button>
      </>
    )
  return (
    <>
      <p className="text-sm font-semibold">Which circle?</p>
      <ul className="flex flex-col gap-2" aria-label="Your circles">
        {circles.map((circle) => {
          const done = sent.includes(circle.id)
          return (
            <li key={circle.id}>
              <Button
                variant="outline"
                className="w-full justify-between"
                disabled={done || invite.isPending}
                onClick={() =>
                  invite.mutate(
                    { circleId: circle.id, userId: other.id },
                    { onSuccess: () => setSent((x) => [...x, circle.id]) },
                  )
                }
              >
                <span className="truncate">{circle.name}</span>
                {done && (
                  <span className="flex items-center gap-1 text-sub text-muted-foreground">
                    <Check aria-hidden className="size-4" /> Invited
                  </span>
                )}
              </Button>
            </li>
          )
        })}
      </ul>
      {sent.length > 0 && <p className="text-sub text-muted-foreground">{first} will be asked whether to join.</p>}
      <FormError error={invite.error} />
      <Button onClick={onDone}>Done</Button>
    </>
  )
}

// An invite from them, offered here as soon as they send it, so joining is one tap away while you're still together.
function InviteFrom({ other }: { other: Face }) {
  const navigate = useNavigate()
  const [later, setLater] = useState<string[]>([])
  const invites = useQuery({
    queryKey: ['invites'],
    queryFn: () => api<{ invites: WaitingInvite[] }>('/invites'),
    refetchInterval: WATCH_MS,
  })
  const invite = invites.data?.invites.find((x) => x.fromId === other.id && !later.includes(x.id))
  if (!invite) return null
  return (
    <section
      aria-label={`Invite to ${invite.family}`}
      className="together-words flex w-full flex-col gap-3 rounded-2xl bg-secondary p-4"
    >
      <p className="text-balance">
        {invite.from} would like you to join <strong className="font-semibold">{invite.family}</strong>.
      </p>
      <div className="flex flex-col gap-2">
        <Button onClick={() => navigate(`/join/${invite.id}`)}>Join {invite.family}</Button>
        <Button variant="ghost" onClick={() => setLater((x) => [...x, invite.id])}>
          Not now
        </Button>
      </div>
    </section>
  )
}
